import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Minus, Check, Warning } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { queueEntry, uuid } from "@/lib/offline";

// Dry-goods Stock In / Stock Out confirmation screen. Reached from the Scan
// page after a variant QR/manual lookup with `?action=add|take_out&loc=...`.
// Reuses POST /api/items/action which mutates the item_location balance and
// writes to the shared `transactions` collection.
export default function ScanDryAction() {
  const { variantId } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const initialAction = sp.get("action") === "take_out" ? "take_out" : "add";
  const locParam = sp.get("loc") || "";

  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(initialAction);
  const [unit, setUnit] = useState("unit");
  const [qty, setQty] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [override, setOverride] = useState(false);

  useEffect(() => {
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    api.get(`/items/${variantId}`).then((r) => setDetail(r.data)).catch((e) => toast.error(formatApiError(e)));
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [variantId]);

  if (!detail) return <div className="p-6 text-sm text-muted-foreground font-mono">Loading item…</div>;

  const v = detail.variant || detail;
  const loc = (detail.locations || []).find((l) => l.location_id === locParam) || (detail.locations || [])[0];
  const upp = Number(v.units_per_pack || 1);
  const ppc = Number(v.packs_per_case || 1);
  const factor = unit === "case" ? upp * ppc : unit === "pack" ? upp : 1;
  const converted = Number(qty || 0) * factor;
  const currentInv = Number(loc?.current_qty ?? 0);
  const projected = action === "add" ? currentInv + converted : currentInv - converted;
  const insufficient = action === "take_out" && projected < 0;
  const isAdmin = user && (user.role === "admin" || user.role === "system_owner");

  const submit = async () => {
    if (Number(qty) <= 0) return toast.error("Quantity must be greater than zero");
    if (insufficient && !override) return toast.error(`Insufficient inventory: ${currentInv} on hand`);
    if (!loc) return toast.error("No location assigned to this item");
    setSaving(true);
    const payload = {
      client_uuid: uuid(),
      variant_id: v.variant_id,
      location_id: loc.location_id,
      action, quantity: Number(qty), unit,
      notes,
      negative_override: !!override && isAdmin,
      source: "qr_scan",
    };
    try {
      if (!online) {
        await queueEntry("item_action", payload);
        toast.success("Saved offline — will sync on reconnect");
      } else {
        await api.post("/items/action", payload);
        toast.success(`${action === "add" ? "Stock In" : "Stock Out"} saved`);
      }
      nav("/");
    } catch (e) {
      if (!online) { await queueEntry("item_action", payload); toast.warning("Saved offline"); }
      else toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      <Link to="/scan" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-3">
        <ArrowLeft size={14} /> Back to scan
      </Link>

      <div className="card-flat border-border p-4 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="label-tech mb-1">Dry Goods Item</div>
            <h1 data-testid="dry-title" className="font-display font-black text-2xl md:text-3xl tracking-tight">{v.product_name}</h1>
            <div className="text-xs font-mono text-muted-foreground mt-2 space-y-0.5">
              <div>ID · {v.product_id}</div>
              <div>Pack · {v.pack_size || `${upp} / pack`} · Case · {upp * ppc}</div>
              {loc && <div>Location · {loc.location_full || loc.location_id}</div>}
            </div>
          </div>
          {loc && <div className="shrink-0 border border-border p-2 bg-white">
            <img alt="QR" src={`${BACKEND_URL}/api/qr/png/${encodeURIComponent(loc.qr_value || v.variant_id)}?size=140`} className="w-20 h-20 md:w-24 md:h-24" />
          </div>}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border">
          <div><div className="label-tech mb-1">On Hand</div><div data-testid="dry-current-qty" className="font-display font-black text-2xl">{currentInv}</div><div className="text-[10px] font-mono text-muted-foreground">units</div></div>
          <div><div className="label-tech mb-1">PAR</div><div className="font-display font-black text-2xl">{loc?.par_level ?? v.par_level ?? "—"}</div></div>
          <div><div className="label-tech mb-1">Vendor</div><div className="font-mono text-xs mt-2">{v.vendor || <span className="italic text-muted-foreground">—</span>}</div></div>
        </div>
      </div>

      <div className="card-flat border-border p-4">
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button data-testid="dry-action-add" onClick={() => setAction("add")} className={`btn-hard h-12 rounded-sm flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-xs ${action === "add" ? "bg-green-600 text-white" : "border border-border hover:bg-muted"}`}><Plus size={16} weight="bold" /> Stock In</button>
          <button data-testid="dry-action-take_out" onClick={() => setAction("take_out")} className={`btn-hard h-12 rounded-sm flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-xs ${action === "take_out" ? "bg-blue-600 text-white" : "border border-border hover:bg-muted"}`}><Minus size={16} weight="bold" /> Stock Out</button>
        </div>

        <div className="grid grid-cols-3 gap-1 mb-3">
          {[["unit", `Units (×1)`], ["pack", `Pack (×${upp})`], ["case", `Case (×${upp * ppc})`]].map(([k, l]) => (
            <button key={k} data-testid={`dry-unit-${k}`} onClick={() => setUnit(k)} className={`h-10 border rounded-sm text-[10px] font-bold uppercase tracking-widest btn-hard ${unit === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>{l}</button>
          ))}
        </div>

        <input
          data-testid="dry-qty"
          type="number" min="0" step="0.01" value={qty}
          onChange={(e) => setQty(e.target.value)}
          placeholder={`Quantity in ${unit}`}
          className="w-full h-14 px-4 border border-border bg-background rounded-sm text-2xl font-display font-black outline-none focus:ring-2 focus:ring-primary mb-3"
        />

        <div className="border border-border rounded-sm p-3 bg-muted/30 font-mono text-xs mb-3 space-y-1" data-testid="dry-preview">
          <div>Converted base: <span className="font-bold">{converted.toFixed(2)} units</span></div>
          <div>{action === "add"
            ? <>Current {currentInv} + {converted.toFixed(2)} = <span data-testid="dry-projected" className="font-bold text-green-600">{projected.toFixed(2)}</span></>
            : <>Current {currentInv} − {converted.toFixed(2)} = <span data-testid="dry-projected" className={`font-bold ${insufficient ? "text-destructive" : "text-blue-600"}`}>{projected.toFixed(2)}</span></>}
          </div>
          {insufficient && <div data-testid="dry-insufficient" className="text-destructive font-bold">Insufficient inventory</div>}
        </div>

        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="w-full px-3 py-2 border border-border bg-background rounded-sm text-sm outline-none focus:ring-2 focus:ring-primary mb-3 font-mono" />

        {insufficient && isAdmin && (
          <label className="flex items-center gap-2 text-xs border border-yellow-500/60 bg-yellow-500/10 rounded-sm p-2 mb-3">
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
            <span><Warning size={12} className="inline" /> Override: allow inventory to go below zero</span>
          </label>
        )}

        <div className="flex gap-2">
          <button onClick={() => nav("/scan")} className="btn-hard h-12 px-4 border border-border font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-muted">Cancel</button>
          <button data-testid="dry-confirm" onClick={submit} disabled={saving || !qty}
            className="btn-hard flex-1 h-12 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2">
            <Check size={16} weight="bold" /> {saving ? "Saving…" : `Confirm ${action === "add" ? "Stock In" : "Stock Out"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
